import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Title, Grid, Box, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as actions from '../../../actions/Lote';
import Info from './Info';
import Fotos from './Fotos';
import ImovelInnerList from '../Imovel/InnerList';
import SmartMapLegend from '../../../components/Map/SmartMapLegend';
import SmartMap, { mapKey } from '../../../components/Map/SmartMapFallback';
import Find from '../Imovel/Find';
import { urlApi } from '../../../utils';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useOptionalNavegadorMap } from '../../../components/Navegador/NavegadorMapContext';
import { DetailSkeleton } from '../../../components/TableSkeleton';

import './View.less';

export default function LoteView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const current = useAppSelector((state) => state.lote.current);
  const publicSettings = useAppSelector((state) => state.publicSettings.settings);
  const loading = useAppSelector((state) => state.container.loading);
  const smartMapApiRef = useRef<unknown>(null);
  const navegadorMap = useOptionalNavegadorMap();
  const inShell = !!navegadorMap;
  const setShellGeoJson = navegadorMap?.setGeoJson;
  const setShellSelectedId = navegadorMap?.setSelectedId;

  // Publica geoJson do lote selecionado no overlay do shell (highlight).
  useEffect(() => {
    if (!setShellGeoJson || !setShellSelectedId) return;
    setShellGeoJson(current?.geoJson ?? null);
    setShellSelectedId(current ? String(current.id) : null);
    return () => { setShellGeoJson(null); };
  }, [setShellGeoJson, setShellSelectedId, current]);

  useEffect(() => {
    if (id) {
      dispatch((actions as any).get(id));
    }
    return () => {
      dispatch((actions as any).clear());
    };
  }, [id, dispatch]);

  const identifyOnExtent = (geographicExtent: number[]) => {
    if (!publicSettings?.mapaInterativoActive) return;

    dispatch((actions as any).identifyOnExtent(geographicExtent, ({ data }: { data: number[] | null }) => {
      if (!data || data.length === 0) {
        notifications.show({ message: 'Nenhum lote encontrado.', color: 'yellow' });
      } else if (data.length === 1) {
        navigate(`/navegador-geofinanceiro/lote/${data[0]}`);
      } else {
        navigate(`/navegador-geofinanceiro?e=${JSON.stringify(geographicExtent).slice(1, -1)}`);
      }
    }));
  };

  return (
    <Box>
      {!inShell && (
        <>
          <Title order={1}>
            {publicSettings?.navegadorGeofinanceiroAbaName} Lote {current && current.inscricao}
          </Title>
          <Find />
        </>
      )}

      {loading && !current ? (
        <Box mt="md"><DetailSkeleton lines={10} /></Box>
      ) : (
      <Grid gap="md" mt="md">
        <Grid.Col span={{ base: 12, md: inShell ? 12 : 2 }} className="geofinanceiro-info">
          <Stack>
            <Info />
            {publicSettings?.imageActive && (
              <div className="geofinanceiro-img-url">
                <a href={publicSettings.imageUrl} target="_blank" rel="noopener noreferrer">
                  <img src={urlApi('PublicSettings/DownloadImage', true)} alt="Imagem com link" />
                </a>
              </div>
            )}
          </Stack>
        </Grid.Col>

        {!inShell && (
          <Grid.Col
            span={{ base: 12, md: 10 }}
            className="geofinanceiromapa"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <Box style={{ position: 'relative', flex: 1, minHeight: 495 }}>
              <SmartMap
                mapKey={mapKey.SMARTPUBLIC}
                onApiReady={(api: unknown) => { smartMapApiRef.current = api; }}
                geoJson={current ? current.geoJson : null}
                identifyOnExtent={identifyOnExtent}
                controls={null}
              />
            </Box>
            <SmartMapLegend />
          </Grid.Col>
        )}
      </Grid>
      )}

      {current && <Box mt="md"><Fotos /></Box>}

      <Box mt="md">
        {current && <ImovelInnerList loteId={current.id} />}
      </Box>
    </Box>
  );
}
